import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useI18n } from '../contexts/I18nContext';

const PasswordReset: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyPasswordResetCode, confirmPasswordReset, sendPasswordResetEmail, checkUserExists, loading } = useAuth();
  const { isDark } = useTheme();
  const { t } = useI18n();
  
  const [step, setStep] = useState<'email' | 'verify' | 'reset' | 'success'>('email');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Obtener el código de reset de la URL
  const resetCode = searchParams.get('oobCode');

  useEffect(() => {
    if (resetCode) {
      // Si hay un código en la URL, verificar automáticamente
      setStep('verify');
      handleVerifyCode();
    } else {
      // Si no hay código, mostrar formulario para ingresar email
      setStep('email');
    }
  }, [resetCode]);

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendResetEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValidEmail(email)) {
      setError(t('pleaseEnterValidEmail'));
      return;
    }

    setIsSendingEmail(true);
    setError(null);

    try {
      await sendPasswordResetEmail(email);
      setSuccess(t('resetEmailSent'));
    } catch (error: any) {
      console.error('Error enviando email de reset:', error);
      
      if (error?.message?.includes('user-not-found')) {
        setError(t('userNotFound'));
      } else {
        setError(t('errorSendingResetEmail'));
      }
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!resetCode) {
      setError(t('noValidResetCodeInUrl'));
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const verifiedEmail = await verifyPasswordResetCode(resetCode);
      setEmail(verifiedEmail);
      setStep('reset');
      setSuccess(t('codeVerifiedSuccessfully'));
    } catch (error: any) {
      console.error('Error verificando código:', error);
      
      if (error?.message?.includes('invalid-action-code')) {
        setError(t('resetCodeExpiredOrInvalid'));
      } else if (error?.message?.includes('expired-action-code')) {
        setError(t('resetCodeExpired'));
      } else {
        setError(t('errorVerifyingResetCode'));
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetCode) {
      setError(t('noValidResetCodeFound'));
      return;
    }

    // Validaciones
    if (newPassword.length < 6) {
      setError(t('passwordTooShort'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      return;
    }

    setIsResetting(true);
    setError(null);

    try {
      await confirmPasswordReset(resetCode, newPassword);
      setStep('success');
      setSuccess(t('passwordUpdatedSuccessfullyMessage'));
    } catch (error: any) {
      console.error('Error actualizando contraseña:', error);
      
      if (error?.message?.includes('invalid-action-code')) {
        setError(t('resetCodeExpiredOrInvalid'));
      } else if (error?.message?.includes('weak-password')) {
        setError(t('passwordTooWeak'));
      } else {
        setError(t('errorUpdatingPassword'));
      }
    } finally {
      setIsResetting(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/');
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-12 ${
      isDark ? 'bg-slate-900' : 'bg-gray-50'
    }`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`w-full max-w-md p-8 rounded-2xl shadow-lg ${
          isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'
        }`}
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/logo.png" 
            alt="Zeta AI" 
            className="w-16 h-16 mx-auto mb-4"
          />
          <h1 className={`text-2xl font-bold ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {t('passwordReset')}
          </h1>
        </div>

        {/* Step 1: Ingreso de email */}
        {step === 'email' && (
          <form onSubmit={handleSendResetEmail} className="space-y-6">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {t('emailLabel')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('emailPlaceholderPasswordReset')}
                className={`w-full px-4 py-3 rounded-xl border ${
                  isDark 
                    ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                required
              />
            </div>

            <motion.button
              type="submit"
              disabled={isSendingEmail || loading}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-medium py-3 px-6 rounded-xl transition-colors duration-200 flex items-center justify-center"
            >
              {isSendingEmail ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t('sending')}
                </>
              ) : (
                t('sendResetEmail')
              )}
            </motion.button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
              >
                {t('backToLogin')}
              </button>
            </div>
          </form>
        )}

        {/* Step 2: Verificación del código */}
        {step === 'verify' && (
          <div className="space-y-6">
            {isVerifying ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {t('verifyingResetCode')}
                </p>
              </div>
            ) : (
              <div className="text-center">
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
                  {resetCode 
                    ? t('verifyingYourResetCode')
                    : t('noValidResetCodeFound')
                  }
                </p>
                {!resetCode && (
                  <button
                    onClick={handleVerifyCode}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-6 rounded-xl transition-colors duration-200"
                  >
                    {t('verifyCode')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Reset de contraseña */}
        {step === 'reset' && (
          <form onSubmit={handlePasswordReset} className="space-y-6">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {t('verifiedEmail')}
              </label>
              <input
                type="email"
                value={email}
                disabled
                className={`w-full px-4 py-3 rounded-xl border ${
                  isDark 
                    ? 'bg-slate-700 border-slate-600 text-gray-300' 
                    : 'bg-gray-100 border-gray-300 text-gray-500'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {t('newPassword')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('newPasswordPlaceholder')}
                  className={`w-full px-4 py-3 pr-12 rounded-xl border ${
                    isDark 
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                    isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {t('confirmPassword')}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('confirmPasswordPlaceholder')}
                  className={`w-full px-4 py-3 pr-12 rounded-xl border ${
                    isDark 
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                    isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <motion.button
              type="submit"
              disabled={isResetting || loading}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-medium py-3 px-6 rounded-xl transition-colors duration-200 flex items-center justify-center"
            >
              {isResetting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t('updating')}
                </>
              ) : (
                t('updatePassword')
              )}
            </motion.button>
          </form>
        )}

        {/* Step 4: Éxito */}
        {step === 'success' && (
          <div className="text-center space-y-6">
            <div className="text-green-500 text-6xl">✅</div>
            <h2 className={`text-xl font-semibold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {t('passwordUpdated')}
            </h2>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {t('passwordUpdatedSuccessfully')}
            </p>
            <motion.button
              onClick={handleGoToLogin}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-6 rounded-xl transition-colors duration-200"
            >
              {t('goToLogin')}
            </motion.button>
          </div>
        )}

        {/* Mensajes de error y éxito */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl"
          >
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </motion.div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl"
          >
            <p className="text-green-600 dark:text-green-400 text-sm">{success}</p>
          </motion.div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {t('needHelp')}{' '}
            <a 
              href="mailto:support@zeta-ai.com" 
              className="text-blue-500 hover:text-blue-600 underline"
            >
              {t('contactSupport')}
            </a>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default PasswordReset;
